import React, { ReactElement, useContext, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  Dashboard as DashboardIcon,
  ShoppingBag as ShoppingBagIcon,
} from "@mui/icons-material";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";

const CONFIRM_CHECKOUT_PAYMENT = gql`
  mutation ConfirmCheckoutPayment($sessionId: String!, $status: String!) {
    confirmCheckoutPayment(sessionId: $sessionId, status: $status) {
      success
      message
    }
  }
`;

export const CheckoutSuccess = (): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";

  usePageTitle(gettext("Payment Success"));

  const [confirmPayment, { loading, data }] = useMutation(
    CONFIRM_CHECKOUT_PAYMENT,
    {
      onError: (err: any) => {
        enqueueSnackbar(err.message, { variant: "error" });
      },
    },
  );

  const confirmedRef = useRef(false);

  useEffect(() => {
    if (sessionId && !confirmedRef.current) {
      confirmedRef.current = true;
      confirmPayment({
        variables: {
          sessionId,
          status: "success",
        },
      });
    } else if (!sessionId) {
      enqueueSnackbar(gettext("No checkout session found."), {
        variant: "warning",
      });
    }
  }, [sessionId, confirmPayment, gettext]);

  const confirmSuccess = (data as any)?.confirmCheckoutPayment?.success;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        px: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: 5,
          textAlign: "center",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.08)",
        }}
      >
        <CardContent sx={{ p: { xs: 4, sm: 6 } }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <CircularProgress size={60} thickness={4} color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {gettext("Confirming payment with Stripe...")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {gettext("Please hold on while we process your transactions.")}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.success.main, 0.09),
                  color: "success.main",
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": {
                      boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.4)}`,
                    },
                    "70%": {
                      boxShadow: `0 0 0 16px ${alpha(theme.palette.success.main, 0)}`,
                    },
                    "100%": {
                      boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}`,
                    },
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 50 }} />
              </Box>

              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 900, mb: 1, color: "text.primary" }}
                >
                  {gettext("Payment Successful!")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {confirmSuccess
                    ? gettext(
                        "Your transaction has completed. The services have been successfully provisioned.",
                      )
                    : gettext(
                        "Payment confirmed but status update is pending. Please verify via your services dashboard.",
                      )}
                </Typography>
              </Box>

              <Divider sx={{ width: "100%" }} />

              {sessionId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {gettext("Session ID:")} {sessionId}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate("/")}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 700,
                  }}
                >
                  {gettext("Go to Dashboard")}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  size="large"
                  startIcon={<ShoppingBagIcon />}
                  onClick={() => navigate("/user-account/services")}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 700,
                  }}
                >
                  {gettext("My Services")}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
